import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { type CommittedLpItem } from "@vega-api/generated/types/committedLpItem";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { formatMoneyShortCompact } from "@vega-core/utils/formatMoney";

import { ReportSectionMessage } from "./ReportSectionMessage";

type Props = {
  committedLps: CommittedLpItem[];
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
};

export function CommittedOrganisationsSection({
  committedLps,
  isError,
  isLoading,
  onRetry,
}: Props) {
  const columns: ColumnDef<CommittedLpItem>[] = useMemo(
    () =>
      [
        {
          accessorKey: "limitedPartnerName",
          header: m.coInvestments_report_committedOrganisations_organisation(),
          cell: ({ row }) => (
            <span className="text-text/primary">
              {row.original.limitedPartnerName}
            </span>
          ),
        },
        {
          accessorKey: "commitmentAmount",
          align: "right",
          header: m.coInvestments_report_committedOrganisations_commitment(),
          cell: ({ row }) =>
            formatMoneyShortCompact(row.original.commitmentAmount as CoreMoney),
        },
        {
          accessorKey: "estimatedReturn",
          align: "right",
          header: m.coInvestments_report_committedOrganisations_estReturn(),
          cell: ({ row }) => (
            <span className="text-text/secondary">
              {row.original.estimatedReturn
                ? formatMoneyShortCompact(
                    row.original.estimatedReturn as CoreMoney,
                  )
                : EMPTY_CELL_VALUE}
            </span>
          ),
        },
      ] satisfies ColumnDef<CommittedLpItem>[],
    [],
  );

  function renderContent() {
    if (isError) {
      return (
        <ReportSectionMessage
          dataTestId="committed-organisations-error"
          message={m.common_error_somethingWentWrong()}
          onRetry={onRetry}
        />
      );
    }

    if (!isLoading && committedLps.length === 0) {
      return (
        <ReportSectionMessage
          dataTestId="committed-organisations-empty"
          message={m.coInvestments_report_committedOrganisations_noData()}
        />
      );
    }

    return (
      <CoreTable
        cellClassName="h-10"
        columns={columns}
        data={committedLps}
        dataTestId="committed-organisations-table"
        isLoading={isLoading}
        wrapperClassName="overflow-y-auto max-h-[calc(11*2.5rem)]"
      />
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="heading-5">
        {m.coInvestments_report_committedOrganisations()}
      </h2>
      {renderContent()}
    </div>
  );
}
